import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, MapPin, Loader2, Flame, Calendar } from "lucide-react";
import LeaderboardTable from "@/components/LeaderboardTable";
import TexasLeaderboardMap from "@/components/TexasLeaderboardMap";
import { useLeaderboard } from "@/hooks/useLeaderboard";
import { fetchEvents } from "@/lib/events";
import type { Event } from "@/types/database";

export default function PublicLeaderboardPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [eventId, setEventId] = useState<string>("");
  const [city, setCity] = useState<string>("");

  // On first load, default to the active event if there is one
  useEffect(() => {
    void fetchEvents({ includeArchived: false }).then((es) => {
      setEvents(es);
      const active = es.find((e) => e.active);
      if (active) setEventId(active.id);
    });
  }, []);

  const filters = useMemo(() => {
    const f: { event_id?: string; city?: string; limit: number } = { limit: 10 };
    if (eventId) f.event_id = eventId;
    if (city) f.city = city;
    return f;
  }, [eventId, city]);

  const { data, loading, error } = useLeaderboard(filters);

  const cities = useMemo(() => {
    const seen = new Set<string>();
    (data?.rows ?? []).forEach((r) => r.city && seen.add(r.city));
    return Array.from(seen).sort();
  }, [data]);

  const headerLabel = useMemo(() => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return "All Events";
    const date = ev.starts_at ? ` · ${new Date(ev.starts_at).toLocaleDateString()}` : "";
    return `${ev.name}${ev.city ? ` · ${ev.city}` : ""}${date}`;
  }, [events, eventId]);

  return (
    <main className="min-h-screen px-4 py-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <Link to="/student/register" className="text-forge-300 text-sm hover:underline inline-flex items-center gap-1">
              <Flame className="w-4 h-4" /> FORGED WeldQuest Texas
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl text-white mt-1">Texas Welding Student Top 10</h1>
            <p className="text-steel-300 text-sm flex items-center gap-1">
              <Calendar className="w-4 h-4 text-forge-400" /> {headerLabel}
            </p>
            <p className="text-steel-400 text-xs mt-1">Correct answers + fastest time wins.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <label className="text-sm text-steel-300 flex items-center gap-1">
              <Filter className="w-4 h-4" /> Event
            </label>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="input py-2">
              <option value="">All events</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>{e.name}{e.city ? ` · ${e.city}` : ""}</option>
              ))}
            </select>
            <label className="text-sm text-steel-300 flex items-center gap-1">
              <Filter className="w-4 h-4" /> City
            </label>
            <select value={city} onChange={(e) => setCity(e.target.value)} className="input py-2">
              <option value="">All Texas</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </header>

        {loading && (
          <div className="text-steel-300 flex items-center gap-2 p-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Pulling latest scores…
          </div>
        )}
        {error && <div className="pill-bad px-3 py-2">{error}</div>}

        {!loading && !error && data && (
          <>
            <LeaderboardTable rows={data.rows} />
            <div>
              <h2 className="font-display text-xl text-white mb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-forge-400" /> Where they're welding from
              </h2>
              <TexasLeaderboardMap rows={data.rows} />
            </div>
          </>
        )}
      </div>
    </main>
  );
}
